import { useCallback, useEffect, useState } from "react";
import {
  parseColumnWidths,
  serializeColumnWidths,
  type ColumnWidths,
} from "../reviewLogic";

/**
 * useColumnWidths — persisted drag-to-resize column widths for the review
 * accounts table (PLO-4818, item 5). Widths are keyed per review so resizing
 * one review's table doesn't bleed into another, and survive reloads via
 * localStorage. All width math + defensive parsing lives in reviewLogic; this
 * hook is just the React state + storage glue.
 */
const storageKey = (reviewId: string) => `accessReview:columnWidths:${reviewId}`;

export const useColumnWidths = (reviewId: string) => {
  const [widths, setWidths] = useState<ColumnWidths>(() => {
    try {
      return parseColumnWidths(localStorage.getItem(storageKey(reviewId)));
    } catch {
      return {};
    }
  });

  // The initializer only runs on first mount, but ReviewDetail (and therefore
  // this hook) is reused across reviewId navigation — so re-load the persisted
  // widths for the new review whenever the id changes, or the previous review's
  // widths bleed through.
  useEffect(() => {
    try {
      setWidths(parseColumnWidths(localStorage.getItem(storageKey(reviewId))));
    } catch {
      setWidths({});
    }
  }, [reviewId]);

  const onResizeColumn = useCallback(
    (updater: (prev: ColumnWidths) => ColumnWidths) => {
      setWidths((prev) => {
        const next = updater(prev);
        try {
          localStorage.setItem(storageKey(reviewId), serializeColumnWidths(next));
        } catch {
          // storage full / unavailable — keep the in-memory widths anyway
        }
        return next;
      });
    },
    [reviewId],
  );

  return { columnWidths: widths, onResizeColumn };
};

export default useColumnWidths;
